import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { PopoverRegistry } from '../popover-registry';
import type { PopoverConfig } from '../popover-registry';
import { PopoverManager } from '../popover-manager';
import { createDisposableListener, Disposable } from '@patsnap/tahiti-core';

@injectable()
export class PopoverRegistryImpl implements PopoverRegistry {

    constructor(
        @inject(PopoverManager)
        private _popoverManager: PopoverManager,
    ) {}

    private _configs: PopoverConfig[] = [];

    private _initialized = false;

    private _hoverTimer: number | null = null;

    private _handleClick(e: Event) {
        const target = e.target as HTMLElement;
        const clickConfigs = this._configs.filter(config => config.trigger === 'click');
        this._showPopover(target, clickConfigs);
    }

    private _handleHover(e: Event) {
        const target = e.target as HTMLElement;
        const hoverConfigs = this._configs.filter(config => config.trigger === 'hover');

        if (!hoverConfigs.length) return;

        if (this._hoverTimer) {
            window.clearTimeout(this._hoverTimer);
        }

        this._hoverTimer = window.setTimeout(() => {
            this._showPopover(target, hoverConfigs);
            this._hoverTimer = null;
        }, 50);
    }

    private _showPopover(target: HTMLElement, configs: PopoverConfig[]) {
        for (const config of configs) {
            const reference = target.closest(config.selector);
            if (reference) {
                this._popoverManager.showPopover(reference as HTMLElement, config);
            }
        }
    }

    register(config: PopoverConfig) {
        const existed = this._configs.find(c => c.selector === config.selector);
        if (existed) {
            return Disposable.create(() => {
                this.unregister(config);
            });
        }
        this._configs.push(config);
        if (!this._initialized) {
            this._initializeEventDelegation();
            this._initialized = true;
        }

        return Disposable.create(() => {
            this.unregister(config);
        });
    }

    unregister(config: PopoverConfig) {
        const index = this._configs.indexOf(config);
        if (index > -1) {
            this._configs.splice(index, 1);

            // 如果没有配置了，清理所有事件监听器
            if (this._configs.length === 0) {
                this._removeEventListeners();
                this._initialized = false;
            }
        }
    }

    dispose() {
        if (this._hoverTimer) {
            window.clearTimeout(this._hoverTimer);
            this._hoverTimer = null;
        }
        this._popoverManager.hideCurrentPopover();
        this._configs = [];
        if (this._initialized) {
            this._removeEventListeners();
            this._initialized = false;
        }
    }

    private _disposes: Disposable[] = [];

    private _initializeEventDelegation() {
        this._disposes.push(
            createDisposableListener(document, 'click', this._handleClick.bind(this)),
            createDisposableListener(document, 'mouseenter', this._handleHover.bind(this), true),
        );
    }

    private _removeEventListeners() {
        if (this._hoverTimer) {
            window.clearTimeout(this._hoverTimer);
            this._hoverTimer = null;
        }
        this._disposes.forEach(dispose => dispose.dispose());
        this._disposes = [];
    }
}
